import React from "react";
import {Formik, Form, Field, ErrorMessage} from "formik";
import * as Yup from "yup";
import "./FormPage.css";
import {Typography, Alert, AlertTitle, Box, Button} from "@mui/material";
import {showSnackbar} from "../../../../../components/SnackBar/SnackBar";
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import LockIcon from '@mui/icons-material/Lock';

export default function PaymentFormComponent(props) {

    const {propertyDetails, originalDetails} = props; // Access tenant files from props

    // Function to partially encrypt sensitive data
    const maskSensitiveData = (value, showFirst = 4, showLast = 4) => {
        if (!value || value.length <= (showFirst + showLast)) {
            return value; // Return as is if too short
        }
        const maskedPortion = '*'.repeat(value.length - showFirst - showLast);
        return value.substring(0, showFirst) + maskedPortion + value.substring(value.length - showLast);
    };

    const validationSchema = Yup.object({
        consumer_secret: Yup.string().required("Consumer secret is required"),
        ho_number: Yup.number().required("HO number is required"),
        pass_key: Yup.string().required("Pass key is required"),
        short_code: Yup.string().required("Short code is required"),
        till_number: Yup.number().required("Till number is required"),
    });

    const initialValues = {
        consumer_secret: maskSensitiveData(propertyDetails.consumer_secret) || "",
        ho_number: propertyDetails.ho_number || "",
        pass_key: maskSensitiveData(propertyDetails.pass_key) || "",
        short_code: propertyDetails.short_code || "",
        till_number: propertyDetails.till_number || "",
    }

    const handleContactSupport = () => {
        const subject = encodeURIComponent("Payment Configuration Change Request");
        const body = encodeURIComponent(`Hello Support Team,

I would like to request changes to my payment gateway configuration for Property ID: ${propertyDetails.property_id || 'N/A'}.

Please contact me to discuss the required changes.

Best regards,
${propertyDetails.property_name || 'Property Owner'}`);

        window.open(`mailto:itorama254@gmail.com?subject=${subject}&body=${body}`, '_blank');

        showSnackbar({
            title: "Email Client Opened",
            message: "Your email client should open with a pre-filled message to itorama254@gmail.com",
            type: "success",
            position: {vertical: "bottom", horizontal: "right"},
        });
    };

    if (propertyDetails.property_id === "") return <div>Loading...</div>;

    return (
        <div className="container">
            {/* Alert Message */}
            <Box sx={{ mb: 3 }}>
                <Alert
                    severity="info"
                    icon={<LockIcon />}
                    sx={{
                        backgroundColor: '#e3f2fd',
                        border: '1px solid #90caf9',
                        '& .MuiAlert-icon': {
                            color: '#1976d2'
                        }
                    }}
                >
                    <AlertTitle sx={{ fontWeight: 'bold' }}>Payment Configuration Locked</AlertTitle>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        For security reasons, payment gateway configurations cannot be modified directly.
                        If you need to update these settings, please contact our customer care team at{' '}
                        <strong>itorama254@gmail.com</strong>.
                    </Typography>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<ContactSupportIcon />}
                        onClick={handleContactSupport}
                        sx={{
                            backgroundColor: '#1976d2',
                            '&:hover': {
                                backgroundColor: '#1565c0'
                            }
                        }}
                    >
                        Contact Customer Care
                    </Button>
                </Alert>
            </Box>

            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={() => {}} // Disabled submit
            >
                {() => (
                    <Form className="form">
                        {/* Editable Information - Now Read Only */}
                        <div className="form-row">
                            <div className="form-group">
                                <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    Consumer Secret:
                                    <LockIcon sx={{ fontSize: 16, color: '#666' }} />
                                </Typography>
                                <Field
                                    type="text"
                                    id="consumer_secret"
                                    name="consumer_secret"
                                    placeholder="Consumer Secret (Encrypted)"
                                    className="light-blue disabled-field sensitive-field"
                                    disabled={true}
                                    readOnly={true}
                                />
                                <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic', mt: 0.5, display: 'block' }}>
                                    * Sensitive data partially hidden for security
                                </Typography>
                                <ErrorMessage
                                    name="consumer_secret"
                                    component="div"
                                    className="error-message"
                                />
                            </div>
                            <div className="form-group">
                                <Typography variant="subtitle1">HO Number:</Typography>
                                <Field
                                    type="number"
                                    id="ho_number"
                                    name="ho_number"
                                    placeholder="Enter HO Number"
                                    className="light-blue disabled-field"
                                    disabled={true}
                                    readOnly={true}
                                />
                                <ErrorMessage
                                    name="ho_number"
                                    component="div"
                                    className="error-message"
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    Pass Key:
                                    <LockIcon sx={{ fontSize: 16, color: '#666' }} />
                                </Typography>
                                <Field
                                    type="text"
                                    id="pass_key"
                                    name="pass_key"
                                    placeholder="Pass Key (Encrypted)"
                                    className="light-blue disabled-field sensitive-field"
                                    disabled={true}
                                    readOnly={true}
                                />
                                <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic', mt: 0.5, display: 'block' }}>
                                    * Sensitive data partially hidden for security
                                </Typography>
                                <ErrorMessage
                                    name="pass_key"
                                    component="div"
                                    className="error-message"
                                />
                            </div>
                            <div className="form-group">
                                <Typography variant="subtitle1">Short Code:</Typography>
                                <Field
                                    type="text"
                                    id="short_code"
                                    name="short_code"
                                    placeholder="Enter Short Code"
                                    className="light-blue disabled-field"
                                    disabled={true}
                                    readOnly={true}
                                />
                                <ErrorMessage
                                    name="short_code"
                                    component="div"
                                    className="error-message"
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <Typography variant="subtitle1">Till Number:</Typography>
                                <Field
                                    type="number"
                                    id="till_number"
                                    name="till_number"
                                    placeholder="Enter Till Number"
                                    className="light-blue disabled-field"
                                    disabled={true}
                                    readOnly={true}
                                />
                                <ErrorMessage
                                    name="till_number"
                                    component="div"
                                    className="error-message"
                                />
                            </div>
                        </div>

                        {/* Disabled Submit Button */}
                        <div className="form-row">
                            <button
                                type="button"
                                className="button primary disabled-button"
                                disabled={true}
                                style={{
                                    opacity: 0.6,
                                    cursor: 'not-allowed',
                                    backgroundColor: '#ccc',
                                    border: '1px solid #999'
                                }}
                            >
                                <LockIcon style={{ marginRight: '8px', fontSize: '16px' }} />
                                Configuration Locked
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
}